import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  useTheme,
  Checkbox,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import LaunchIcon from "@mui/icons-material/Launch";
import LinkIcon from "@mui/icons-material/Link";
import CheckIcon from '@mui/icons-material/Check';
import Fade from '@mui/material/Fade';
import CancelIcon from "@mui/icons-material/Cancel";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import toast from "react-hot-toast";
import { getData, vincularMultiImobiliarios } from "../../../services/api";

interface DialogVincularMultiImobiliarioProps {
  openDialog: boolean;
  loteId: number;
  idPessoa: number;
  handleCloseDialog: () => void;
  onVinculoConcluido?: () => void;
  minimized: boolean;
  setMinimized: (value: boolean) => void;
}

export function DialogVincularMultiImobiliario({
  openDialog,
  loteId,
  idPessoa,
  handleCloseDialog,
  onVinculoConcluido,
  minimized,
  setMinimized
}: DialogVincularMultiImobiliarioProps) {
  const theme = useTheme();
  const [options, setOptions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openSelector, setOpenSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState<Map<string, any>>(new Map());
  const [allImobiliarios, setAllImobiliarios] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedIds = Array.from(selectedMap.keys());

  useEffect(() => {
    if (!openDialog) return;
    let active = true;

    (async () => {
      try {
        setLoading(true);
        const data = await getData(loteId, 1, "imobiliario", searchTerm);
        if (active && data?.imobiliario) {
          const incoming = data.imobiliario;
          const existingSelected = Array.from(selectedMap.values());

          const mergedMap = new Map();
          for (const item of existingSelected) {
            const id = String(item.numero_cadastro ?? item.id);
            mergedMap.set(id, item);
          }
          for (const item of incoming) {
            const id = String(item.numero_cadastro ?? item.id);
            mergedMap.set(id, item);
          }

          setOptions(Array.from(mergedMap.values()));
        }
      } catch (error) {
        console.error("Erro ao buscar imobiliários:", error);
      } finally {
        setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [openDialog, loteId, searchTerm]);

  async function handleVincular() {
    try {
      setSubmitting(true);

      const idsImobiliariosToSend = allImobiliarios
        ? ["__allImobiliarios__"]
        : Array.from(selectedMap.values()).map((item) => item.id);

      await vincularMultiImobiliarios({
        id_pessoa: idPessoa,
        id_lote: loteId,
        ids_imobiliarios: idsImobiliariosToSend,
      });

      toast.success("Vínculo realizado com sucesso!");
      setSearchTerm("");
      setAllImobiliarios(false);
      handleCloseDialog();
      onVinculoConcluido?.();
    } catch (error) {
      console.error("Erro ao vincular múltiplos imobiliários:", error);
      toast.error("Falha ao realizar o vínculo!");
    } finally {
      setSubmitting(false);
    }
  }

  const handleToggle = (item: any) => {
    const id = String(item.numero_cadastro ?? item.id);
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, item);
      }
      return next;
    });
  };

  const handleCancel = () => {
    setSelectedMap(new Map());
    setSearchTerm("");
    setAllImobiliarios(false);
    setMinimized(false);
    handleCloseDialog();
  };

  const filteredOptions = options.filter((item) =>
    (item.numero_cadastro ?? item.id)
      .toString()
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const allVisibleSelected = filteredOptions.every((item) => {
    const id = String(item.numero_cadastro ?? item.id);
    return selectedMap.has(id);
  });

  const handleToggleAllVisible = () => {
    const next = new Map(selectedMap);
    if (allVisibleSelected) {
      for (const item of filteredOptions) {
        const id = String(item.numero_cadastro ?? item.id);
        next.delete(id);
      }
    } else {
      for (const item of filteredOptions) {
        const id = String(item.numero_cadastro ?? item.id);
        next.set(id, item);
      }
    }
    setSelectedMap(next);
  };

  useEffect(() => {
    if (openDialog && !minimized) {
      setOpenSelector(false);
    }
  }, [openDialog, minimized]);

  return (
    <>
      <Dialog
        fullWidth
        open={openDialog && !minimized}
        onClose={(event: React.SyntheticEvent, reason: string) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            event.preventDefault();
  
            if (submitting) {
              setMinimized(true);
            } else {
              handleCancel();
            }
          } else {
            handleCancel();
          }
        }}
      >
        <DialogTitle>Vincular o proprietário a vários imóveis</DialogTitle>

        <DialogContent>
          {submitting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={32} color="success" sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Realizando o vínculo... Por favor aguarde.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Box
                tabIndex={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  minHeight: 56,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: 'text.primary',
                  },
                  '&:focus-visible': {
                    borderColor: 'primary.main',
                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}44`,
                    outline: 'none',
                  },
                }}
                onClick={() => setOpenSelector(true)}
              >
                <Typography variant="body1" color="text.primary">
                  {allImobiliarios
                    ? 'Todos os Imobiliários do Lote selecionados'
                    : selectedIds.length > 0
                      ? `${selectedIds.length} ${selectedIds.length > 1 ? 'Imóveis selecionados' : 'Imóvel selecionado'}`
                      : 'Clique aqui para selecionar os imóveis'}
                </Typography>
              </Box>

              <Dialog
                open={openSelector}
                onClose={() => setOpenSelector(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Selecionar Imóveis</DialogTitle>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    ml: 3.2,
                    mr: 3.2,
                    px: 1,
                    mb: 2,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s, color 0.2s',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => {
                    const next = !allImobiliarios;
                    setAllImobiliarios(next);
                    if (next) setSelectedMap(new Map());
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={allImobiliarios}
                    tabIndex={-1}
                    disableRipple
                    color="success"
                  />
                  <Typography variant="body1" color="text.primary" sx={{ ml: 1 }}>
                    Todos os Imobiliários do Lote
                  </Typography>
                </Box>

                {!allImobiliarios ? (
                  <DialogContent dividers>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <TextField
                        placeholder="Pesquisar imóveis"
                        size="small"
                        fullWidth
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'divider',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'primary.main',
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <IconButton
                        size="small"
                        color="success"
                        onClick={handleToggleAllVisible}
                      >
                        {allVisibleSelected ? (
                          <CheckBoxIcon fontSize="medium" />
                        ) : (
                          <CheckBoxOutlineBlankIcon fontSize="medium" />
                        )}
                      </IconButton>
                    </Box>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                        <CircularProgress size={32} color="success" />
                      </Box>
                    ) : filteredOptions.length === 0 ? (
                      <Fade in timeout={300}>
                        <Box sx={{ textAlign: 'center', mt: 4, mb: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Nenhum imóvel encontrado{searchTerm ? ` para "${searchTerm}"` : ""}.
                          </Typography>
                        </Box>
                      </Fade>
                    ) : (
                      <>
                        <List dense>
                          {filteredOptions.map((option) => {
                            const id = String(option.numero_cadastro ?? option.id);
                            const label = option.numero_cadastro
                              ? `Número Cadastro: ${option.numero_cadastro}`
                              : `ID - ${option.id}`;
                            const checked = selectedIds.includes(id);

                            return (
                              <ListItem key={id} button onClick={() => handleToggle(option)} sx={{ px: 1 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <Checkbox
                                    edge="start"
                                    checked={checked}
                                    tabIndex={-1}
                                    disableRipple
                                    color="success"
                                  />
                                </ListItemIcon>
                                <ListItemText primary={label} />
                              </ListItem>
                            );
                          })}
                        </List>
                      </>
                    )}
                  </DialogContent>
                ) : null}
                <DialogActions>
                  <Button
                    onClick={() => setOpenSelector(false)}
                    startIcon={<CheckIcon />}
                    variant="contained"
                    color="success"
                  >
                    Confirmar
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {submitting ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                px: 2,
                py: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Você pode clicar em <strong>Fechar</strong> — o vínculo continuará em segundo plano.
                <br /> Apenas evite fechar ou recarregar a página.
              </Typography>

              <Button
                onClick={() => setMinimized(true)}
                startIcon={<CancelIcon />}
                color="success"
                variant="outlined"
              >
                Fechar
              </Button>
            </Box>
          ) : (
            <>
              <Button
                onClick={handleCancel}
                startIcon={<CancelIcon />}
                color="success"
                variant="outlined"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleVincular}
                startIcon={<LinkIcon />}
                color="success"
                variant="contained"
                disabled={(selectedIds.length === 0 && !allImobiliarios) || submitting}
              >
                Vincular
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {minimized && (
        <Box
          sx={{
            position: 'fixed',
            top: 68,
            right: 16,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 3,
            borderRadius: 1,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            zIndex: 1300,
          }}
        >
          <CircularProgress size={20} color="success" />
          <Typography variant="body2" color="textPrimary">
            Realizando o vínculo... Por favor aguarde.
          </Typography>
          <IconButton size="small" color="success" onClick={() => setMinimized(false)}>
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </>
  );
}
